import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { SearchInput } from "@/components/molecules/SearchInput";
import { FILTER_ACTIVE_CLASS, FilterBarBoundary, useFilterHighlight } from "@/components/molecules/FilterBar";

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Secondary line under the label inside the dropdown. */
  subtitle?: string;
  disabled?: boolean;
}

interface Props {
  label?: string;
  hideLabel?: boolean;
  required?: boolean;
  /** Selected option values. */
  value: string[];
  onChange: (next: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  loading?: boolean;
  /** Empty-list message inside the dropdown. */
  emptyText?: string;
}

/**
 * Multi-select counterpart to {@link MenuSelect}, sharing its trigger chrome and
 * portalled dropdown so the two read as one control in a form. The differences
 * are inherent to picking several things: the panel stays open while you toggle
 * options, and the trigger shows the current selection as removable chips
 * instead of a single label.
 */
export function MultiSelect({
  label,
  hideLabel = false,
  required = false,
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled,
  enableSearch = false,
  searchPlaceholder = "Search…",
  loading = false,
  emptyText = "No options",
}: Props) {
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [placement, setPlacement] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    openUp: boolean;
  } | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const debouncedFilter = useDebouncedValue(filterText.trim(), enableSearch ? 250 : 0);
  const canOpen = !disabled && (options.length > 0 || loading);

  useEffect(() => {
    if (!open) setFilterText("");
  }, [open]);

  const visibleOptions = useMemo(() => {
    const q = debouncedFilter.toLowerCase();
    if (!enableSearch || !q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q) ||
        (o.subtitle?.toLowerCase().includes(q) ?? false),
    );
  }, [enableSearch, options, debouncedFilter]);

  const updatePlacement = useCallback(() => {
    const el = triggerRef.current ?? rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportPad = 8;
    const gap = 6;
    const preferredMax = enableSearch ? 256 : 208;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPad;
    const spaceAbove = rect.top - viewportPad;
    const openUp = spaceBelow < 120 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(preferredMax, openUp ? spaceAbove - gap : spaceBelow - gap);
    setPlacement({
      top: openUp ? rect.top - gap : rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(96, maxHeight),
      openUp,
    });
  }, [enableSearch]);

  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return;
    }
    updatePlacement();
    window.addEventListener("resize", updatePlacement);
    window.addEventListener("scroll", updatePlacement, true);
    return () => {
      window.removeEventListener("resize", updatePlacement);
      window.removeEventListener("scroll", updatePlacement, true);
    };
  }, [open, updatePlacement, visibleOptions.length]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const labelByValue = useMemo(
    () => new Map(options.map((o) => [o.value, o.label])),
    [options],
  );

  const toggle = (optionValue: string) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue],
    );
  };

  const filterActive = useFilterHighlight(value.length > 0);

  const triggerShellClass = cn(
    "w-full flex items-center gap-2 min-h-[42px] pl-4 pr-3 py-2 text-left text-sm rounded-xl border bg-white shadow-sm outline-none transition-all",
    "border-bridge-border/80 hover:border-bridge-accent/35",
    filterActive && FILTER_ACTIVE_CLASS,
    open ? "ring-2 ring-bridge-accent/25 border-bridge-accent" : "",
    !canOpen && "opacity-60 cursor-not-allowed bg-bridge-bg/50",
  );

  return (
    <div ref={rootRef} className="relative w-full self-start">
      {!hideLabel && label ? (
        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-bridge-muted">
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </span>
      ) : null}

      <button
        ref={triggerRef}
        type="button"
        disabled={!canOpen}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => canOpen && setOpen((was) => !was)}
        className={triggerShellClass}
      >
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
          {value.length === 0 ? (
            <span className="text-bridge-subtle">{placeholder}</span>
          ) : (
            value.map((v) => (
              // Chips are spans, not buttons — a button inside the trigger
              // button would be invalid markup; the click is handled here and
              // stopped from reaching the trigger so it doesn't also toggle.
              <span
                key={v}
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(v);
                }}
                className="inline-flex max-w-full items-center gap-1 rounded-md border border-bridge-accent/50 bg-bridge-accent/10 px-1.5 py-0.5 text-xs font-medium text-bridge-accent transition-colors hover:bg-bridge-accent/20"
              >
                <span className="truncate">{labelByValue.get(v) ?? v}</span>
                <X size={11} className="shrink-0" />
              </span>
            ))
          )}
        </span>
        <ChevronDown
          className={cn("h-4 w-4 shrink-0 text-bridge-muted transition-transform duration-200", open && "rotate-180")}
          aria-hidden
        />
      </button>

      {open &&
        placement &&
        createPortal(
          <FilterBarBoundary>
            <div
              ref={panelRef}
              className="fixed z-[70] overflow-hidden rounded-xl border border-bridge-border/70 bg-white shadow-xl"
              style={{
                top: placement.openUp ? undefined : placement.top,
                bottom: placement.openUp ? window.innerHeight - placement.top : undefined,
                left: placement.left,
                width: placement.width,
              }}
            >
              {enableSearch ? (
                <div className="shrink-0 border-b border-bridge-border/70 bg-white p-2">
                  <SearchInput
                    value={filterText}
                    onChange={setFilterText}
                    placeholder={searchPlaceholder}
                    autoFocus
                  />
                </div>
              ) : null}

              <ul
                role="listbox"
                aria-multiselectable
                className="overflow-auto bg-white py-1"
                style={{ maxHeight: placement.maxHeight - (enableSearch ? 52 : 0) }}
              >
                {loading && visibleOptions.length === 0 ? (
                  <li className="px-3 py-3 text-center text-sm text-bridge-muted">Loading…</li>
                ) : visibleOptions.length === 0 ? (
                  <li className="px-3 py-3 text-center text-sm text-bridge-muted">
                    {debouncedFilter ? "No matches" : emptyText}
                  </li>
                ) : (
                  visibleOptions.map((opt) => {
                    const isSelected = value.includes(opt.value);
                    return (
                      <li key={opt.value} role="presentation">
                        <button
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          disabled={opt.disabled}
                          // Deliberately does not close: picking several values
                          // one dropdown-open at a time is the whole point.
                          onClick={() => toggle(opt.value)}
                          className={cn(
                            "flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors",
                            opt.disabled
                              ? "cursor-not-allowed text-bridge-muted opacity-50"
                              : isSelected
                                ? "bg-bridge-accent/12 font-medium text-bridge-accent"
                                : "text-bridge-fg hover:bg-bridge-bg",
                          )}
                        >
                          <span
                            className={cn(
                              "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                              isSelected
                                ? "border-bridge-accent bg-bridge-accent text-white"
                                : "border-bridge-border bg-white",
                            )}
                          >
                            {isSelected && <Check size={12} strokeWidth={3} />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate">{opt.label}</span>
                            {opt.subtitle ? (
                              <span
                                className={cn(
                                  "mt-0.5 block truncate text-[10px] uppercase tracking-widest",
                                  isSelected ? "text-bridge-accent/70" : "text-bridge-muted opacity-70",
                                )}
                              >
                                {opt.subtitle}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </FilterBarBoundary>,
          document.body,
        )}
    </div>
  );
}
